'use client';

import React, { useMemo } from 'react';
import { asInputValue, type FieldValue } from '../formFieldTypes';

/**
 * MeasurementField — Numeric input with unit badge, min/max gauge, auto pass/fail
 * 
 * Used for compliance forms requiring precise readings:
 * - Electrical CoC: Earth continuity (Ω), Insulation resistance (MΩ)
 * - Gas CoC: Pressure tests (kPa)
 * - HVAC: Temperature (°C), Airflow (m³/s)
 * 
 * options.unit            — Display unit (Ω, MΩ, V, A, etc.)
 * validation.min_value    — Minimum acceptable reading
 * validation.max_value    — Maximum acceptable reading
 * validation.tolerance    — ±% tolerance band
 * options.precision       — Decimal places (default: 2)
 * validation.pass_criteria — Human-readable pass condition
 * validation.fail_action  — 'block' | 'warn' | 'flag'
 * options.show_gauge      — Display visual range indicator
 */

type MeasurementOptions = {
      unit?: string;
      precision?: number;
      show_gauge?: boolean;
      auto_pass_fail?: boolean;
      min?: number;
      max?: number;
      tolerance?: number;
      [key: string]: unknown;
};

type MeasurementValidation = {
      min?: number;
      max?: number;
      min_value?: number;
      max_value?: number;
      tolerance?: number;
      pass_criteria?: string;
      fail_action?: 'block' | 'warn' | 'flag';
      [key: string]: unknown;
};

interface MeasurementFieldProps {
  field: {
    key: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    readonly?: boolean;
    validation?: unknown;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

export const MeasurementField: React.FC<MeasurementFieldProps> = ({ field, value, onChange, isDark }) => {
  const opts = (field.options ?? {}) as MeasurementOptions;
  const validation = (field.validation ?? {}) as MeasurementValidation;
  const unit = opts?.unit || '';
  const precision = opts?.precision ?? 2;
  // Read limits from validation (canonical) with fallback to options (legacy builder output)
  const minVal = validation?.min_value ?? validation?.min ?? opts?.min;
  const maxVal = validation?.max_value ?? validation?.max ?? opts?.max;
  const tolerance = validation?.tolerance ?? opts?.tolerance;
  const failAction = validation?.fail_action || 'warn';
  const showGauge = opts?.show_gauge !== false && (minVal !== undefined || maxVal !== undefined);
  const autoPassFail = opts?.auto_pass_fail !== false;

  const numericValue = value !== null && value !== undefined && value !== '' ? parseFloat(String(value)) : null;

  // Evaluate pass/fail status
  const status = useMemo(() => {
    if (numericValue === null || !autoPassFail) return 'pending';
    
    let effectiveMin = minVal;
    let effectiveMax = maxVal;

    // Apply tolerance band
    if (tolerance !== undefined && tolerance > 0) {
      if (effectiveMin !== undefined) effectiveMin = effectiveMin * (1 - tolerance / 100);
      if (effectiveMax !== undefined) effectiveMax = effectiveMax * (1 + tolerance / 100);
    }

    if (effectiveMin !== undefined && numericValue < effectiveMin) return 'fail';
    if (effectiveMax !== undefined && numericValue > effectiveMax) return 'fail';
    return 'pass';
  }, [numericValue, minVal, maxVal, tolerance, autoPassFail]);

  // Gauge position (0-100%)
  const gaugePercent = useMemo(() => {
    if (numericValue === null || minVal === undefined || maxVal === undefined) return null;
    const range = maxVal - minVal;
    if (range <= 0) return 50;
    const clamped = Math.min(Math.max(numericValue, minVal - range * 0.1), maxVal + range * 0.1);
    return ((clamped - (minVal - range * 0.1)) / (range * 1.2)) * 100;
  }, [numericValue, minVal, maxVal]);

  const statusColors = {
    pass: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-300 dark:border-emerald-700', text: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300' },
    fail: { bg: failAction === 'block' ? 'bg-red-50 dark:bg-red-950/30' : 'bg-amber-50 dark:bg-amber-950/30', border: failAction === 'block' ? 'border-red-300 dark:border-red-700' : 'border-amber-300 dark:border-amber-700', text: failAction === 'block' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400', badge: failAction === 'block' ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' : 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300' },
    pending: { bg: '', border: isDark ? 'border-gray-600' : 'border-gray-300', text: isDark ? 'text-gray-400' : 'text-gray-500', badge: '' },
  };

  const colors = statusColors[status];

  return (
    <div className="space-y-1.5">
      <div className={`relative rounded-lg border ${colors.border} ${status !== 'pending' ? colors.bg : ''} transition-colors`}>
        <div className="flex items-center">
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            step={Math.pow(10, -precision).toString()}
            value={asInputValue(value)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') { onChange(null); return; }
              // Allow intermediate input like "2." or "-" while typing
              if (raw === '-' || raw === '.' || raw === '-.') { onChange(raw); return; }
              // Keep as string while user is mid-edit (trailing dot or trailing decimal zeros)
              if (/^-?\d+\.$/.test(raw) || /^-?\d+\.\d*0+$/.test(raw)) { onChange(raw); return; }
              const num = parseFloat(raw);
              onChange(isNaN(num) ? raw : num);
            }}
            onFocus={(e) => {
              // On Capacitor/mobile, ensure the input is properly focused
              e.target.setAttribute('autocomplete', 'off');
              e.target.setAttribute('autocorrect', 'off');
            }}
            placeholder={field.placeholder || `Enter value${unit ? ` (${unit})` : ''}`}
            className={`block w-full rounded-lg px-3 py-2.5 text-sm border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-teal-500 ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'} ${unit ? 'pr-16' : ''}`}
            style={{ touchAction: 'manipulation' }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            required={field.required}
            readOnly={field.readonly}
            disabled={field.readonly}
          />
          {unit && (
            <div className={`absolute inset-y-0 right-0 flex items-center pr-3`}>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {unit}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pass/Fail badge */}
      {status !== 'pending' && autoPassFail && (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
            {status === 'pass' ? (
              <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>Pass</>
            ) : (
              <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 15.75h.007v.008H12v-.008z" /></svg>{failAction === 'block' ? 'FAIL — Blocks submission' : failAction === 'flag' ? 'Flagged' : 'Warning'}</>
            )}
          </span>
          {validation?.pass_criteria && (
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              ({validation.pass_criteria})
            </span>
          )}
        </div>
      )}

      {/* Visual gauge */}
      {showGauge && numericValue !== null && gaugePercent !== null && (
        <div className="relative h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
          {/* Acceptable range band */}
          {minVal !== undefined && maxVal !== undefined && (
            <div
              className="absolute h-full bg-emerald-200 dark:bg-emerald-900/50"
              style={{
                left: `${((minVal - (minVal - (maxVal - minVal) * 0.1)) / ((maxVal - minVal) * 1.2)) * 100}%`,
                width: `${((maxVal - minVal) / ((maxVal - minVal) * 1.2)) * 100}%`,
              }}
            />
          )}
          {/* Current value marker */}
          <div
            className={`absolute top-0 h-full w-1 rounded-full transition-all ${status === 'pass' ? 'bg-emerald-500' : 'bg-red-500'}`}
            style={{ left: `${Math.min(Math.max(gaugePercent, 0), 100)}%` }}
          />
        </div>
      )}

      {/* Range hint */}
      {(minVal !== undefined || maxVal !== undefined) && (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {minVal !== undefined && maxVal !== undefined
            ? `Acceptable range: ${minVal}–${maxVal}${unit ? ` ${unit}` : ''}${tolerance ? ` (±${tolerance}%)` : ''}`
            : minVal !== undefined
              ? `Min: ${minVal}${unit ? ` ${unit}` : ''}`
              : `Max: ${maxVal}${unit ? ` ${unit}` : ''}`
          }
        </p>
      )}
    </div>
  );
};

export default MeasurementField;
